import type { Sensor } from "@core/apis";

import type { NonNullableDateRange } from "../hooks/useGraphControlState";
import type { RadiatorSensorDataMap } from "../hooks/useRadiatorCurveData";
import { useRadiatorCurveData } from "../hooks/useRadiatorCurveData";
import type { Resolution } from "../shared/types";
import { RadiatorCurveChart } from "./RadiatorCurveChart";

type RadiatorCurveSectionProps = Readonly<{
  substationId: string;
  dateRange: NonNullableDateRange;
  resolution?: Resolution;
  /** Network UID needed by Platform for weather coordinate lookups. Optional for PropAI. */
  networkUid?: string;
  /** Pre-fetched sensors. When omitted with fetchSensors=true, the hook self-fetches. */
  radiatorSensors?: Sensor[];
  radiatorSensorsLoading?: boolean;
  /** Pre-fetched sensor data. When omitted with fetchSensorData=true, the hook self-fetches. */
  radiatorSensorData?: RadiatorSensorDataMap;
  radiatorSensorDataLoading?: boolean;
  /** When true, the hook fetches sensors internally. Default: false (uses provided props). */
  fetchSensors?: boolean;
  /** When true, the hook fetches sensor data internally. Default: false (uses provided props). */
  fetchSensorData?: boolean;
}>;

export function RadiatorCurveSection({
  substationId,
  dateRange,
  resolution,
  networkUid,
  radiatorSensors,
  radiatorSensorsLoading = false,
  radiatorSensorData,
  radiatorSensorDataLoading = false,
  fetchSensors = false,
  fetchSensorData = false,
}: RadiatorCurveSectionProps) {
  const { curvePoints, supplyDataPoints, isLoading } = useRadiatorCurveData({
    substationId,
    networkUid,
    dateRange: { start: dateRange.startDate, end: dateRange.endDate },
    sensors: radiatorSensors,
    sensorsLoading: radiatorSensorsLoading,
    sensorDataByUid: radiatorSensorData,
    sensorDataLoading: radiatorSensorDataLoading,
    fetchSensors,
    fetchSensorData,
    resolution,
  });

  return (
    <RadiatorCurveChart
      chartId={`radiator-curve-${substationId}`}
      curvePoints={curvePoints}
      supplyDataPoints={supplyDataPoints}
      isLoading={isLoading}
      showExport={!isLoading && curvePoints.length > 0}
    />
  );
}
